import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Stack,
    Chip,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardActionArea,
    CardContent,
    Dialog,
    DialogContent,
    IconButton,
    Alert,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import {
    VideoLibrary as VideosIcon,
    Search as SearchIcon,
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import VideoService from "../services/Video.service";
import { stringToColor } from "../utils/colorUtils";

function formatDate(dateStr) {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

// ── Thumbnail ────────────────────────────────────────────────────────────────

const VideoThumbnail = ({ color, image }) => (
    <Box
        sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            background: image
                ? undefined
                : `linear-gradient(135deg, ${color} 0%, ${color}99 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
        }}
    >
        {image ? (
            <Box
                component="img"
                src={image}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
        ) : (
            <PlayArrowIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.85)" }} />
        )}
    </Box>
);

// ── Video Card ───────────────────────────────────────────────────────────────

const VideoCard = ({ video, color, onPlay }) => (
    <Card sx={{ height: "100%", boxShadow: "none", display: "flex", flexDirection: "column" }}>
        <CardActionArea onClick={() => onPlay(video)} sx={{ alignItems: "flex-start" }}>
            <VideoThumbnail color={color} image={video.thumbnail_detail?.document_url} />
            <CardContent sx={{ width: "100%" }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        lineHeight: 1.3,
                        mb: 0.5,
                    }}
                >
                    {video.title}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                >
                    {formatDate(video.created)}
                </Typography>
            </CardContent>
        </CardActionArea>
    </Card>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function Videos() {
    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState("All");
    const [playing, setPlaying] = useState(null);

    const [videos, setVideos] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const [videosRes, categoriesRes] = await Promise.all([
                    VideoService.list(),
                    VideoService.listCategories(),
                ]);
                if (cancelled) return;
                setVideos(Array.isArray(videosRes.data) ? videosRes.data : []);
                setCategories(
                    Array.isArray(categoriesRes.data) ? categoriesRes.data : [],
                );
            } catch {
                if (!cancelled) setError("Failed to load videos.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const colorByCategory = useMemo(
        () =>
            Object.fromEntries(
                categories.map((c) => [
                    c.description,
                    c.color || stringToColor(c.description),
                ]),
            ),
        [categories],
    );

    const categoryCounts = useMemo(() => {
        const counts = {};
        videos.forEach((v) => {
            const name = v.category_detail?.description;
            if (name) counts[name] = (counts[name] || 0) + 1;
        });
        return counts;
    }, [videos]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return videos.filter((v) => {
            const categoryName = v.category_detail?.description || "";
            if (activeCategory !== "All" && categoryName !== activeCategory) return false;
            if (!q) return true;
            return (
                v.title.toLowerCase().includes(q) ||
                categoryName.toLowerCase().includes(q)
            );
        });
    }, [videos, search, activeCategory]);

    const sections = useMemo(
        () =>
            categories
                .map((cat) => ({
                    ...cat,
                    videos: filtered.filter(
                        (v) => v.category_detail?.description === cat.description,
                    ),
                }))
                .filter((s) => s.videos.length > 0),
        [categories, filtered],
    );

    const uncategorized = useMemo(
        () => filtered.filter((v) => !v.category_detail),
        [filtered],
    );

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    p: 2.5,
                    mb: 2,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <VideosIcon sx={{ fontSize: 36, color: "secondary.main" }} />
                    <Box>
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                            Training Videos
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {videos.length} video{videos.length === 1 ? "" : "s"} across{" "}
                            {categories.length} categor{categories.length === 1 ? "y" : "ies"}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search videos..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: { sm: 220 }, flexShrink: 0 }}>
                        <Select
                            value={activeCategory}
                            onChange={(e) => setActiveCategory(e.target.value)}
                            renderValue={(value) => {
                                if (value === "All") return "All Categories";
                                const color = colorByCategory[value];
                                return (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                bgcolor: color,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <span>{value}</span>
                                    </Stack>
                                );
                            }}
                        >
                            <MenuItem value="All">All Categories</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.description}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                bgcolor: colorByCategory[cat.description],
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Box component="span" sx={{ flexGrow: 1 }}>
                                            {cat.description}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {categoryCounts[cat.description] || 0}
                                        </Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {/* Sections */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : sections.length === 0 && uncategorized.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 6,
                        textAlign: "center",
                    }}
                >
                    <VideosIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary">
                        {videos.length === 0
                            ? "No videos have been uploaded yet."
                            : "No videos match your search."}
                    </Typography>
                </Paper>
            ) : (
                <>
                    {sections.map((section) => (
                        <Box key={section.id} sx={{ mb: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        bgcolor: colorByCategory[section.description],
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                                >
                                    {section.description}
                                </Typography>
                            </Stack>
                            <Grid container spacing={2}>
                                {section.videos.map((video) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                                        <VideoCard
                                            video={video}
                                            color={colorByCategory[section.description]}
                                            onPlay={setPlaying}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}

                    {uncategorized.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        bgcolor: "text.disabled",
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                                >
                                    Uncategorized
                                </Typography>
                            </Stack>
                            <Grid container spacing={2}>
                                {uncategorized.map((video) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                                        <VideoCard
                                            video={video}
                                            color="#6b7280"
                                            onPlay={setPlaying}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </>
            )}

            {/* Player dialog */}
            <Dialog
                open={Boolean(playing)}
                onClose={() => setPlaying(null)}
                maxWidth="md"
                fullWidth
            >
                {playing && (
                    <DialogContent sx={{ p: 0 }}>
                        <Box sx={{ position: "relative" }}>
                            <Box
                                component="video"
                                src={playing.document_url}
                                controls
                                autoPlay
                                poster={playing.thumbnail_detail?.document_url || undefined}
                                sx={{
                                    width: "100%",
                                    aspectRatio: "16 / 9",
                                    display: "block",
                                    bgcolor: "#000",
                                }}
                            />
                            <IconButton
                                onClick={() => setPlaying(null)}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    bgcolor: "rgba(0,0,0,0.5)",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ p: 2.5 }}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ fontFamily: "Montserrat, sans-serif", mb: 0.5 }}
                            >
                                {playing.title}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                {playing.category_detail && (
                                    <Chip
                                        label={playing.category_detail.description}
                                        size="small"
                                        sx={{
                                            bgcolor: colorByCategory[playing.category_detail.description],
                                            color: "#fff",
                                            fontWeight: 600,
                                        }}
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {formatDate(playing.created)}
                                </Typography>
                            </Stack>
                        </Box>
                    </DialogContent>
                )}
            </Dialog>
        </Box>
    );
}
